import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const RTL_LANGUAGES = ['ar'];

const getSavedLanguage = () => {
    if (typeof window === 'undefined') return 'en';
    return window.localStorage.getItem('userLanguage') || 'en';
};

const syncDocumentLanguage = (language: string) => {
    if (typeof document === 'undefined') return;
    const currentLanguage = language || 'en';
    document.documentElement.lang = currentLanguage;
    document.documentElement.dir = RTL_LANGUAGES.includes(currentLanguage.substring(0, 2)) ? 'rtl' : 'ltr';
};

const adminTranslations = {
    en: {
        thirty_days: '30 days',
        twenty_weeks: '20 weeks',
        twelve_months: '12 months',
        new_users: 'New users',
        paid_users: 'Paid users',
        paid_revenue: 'Paid revenue',
        active_users: 'Active users',
        api_activity: 'API activity',
        authenticated_api_requests: 'Authenticated API Requests',
        trend_overview: 'Trend Overview',
        trend_description: 'Normalized per metric so different scales can be compared.',
        trend_chart_label: 'Admin metrics trend chart',
        payment_channels: 'Payment Channels',
        unclassified: 'Unclassified',
        orders: 'orders',
        bucket_details: 'Bucket Details',
        raw_rows: 'Read-only raw API rows.',
        buckets: '{{count}} buckets',
        period: 'Period',
        new: 'New',
        paid: 'Paid users',
        revenue: 'Revenue',
        active: 'Active',
        auth_api: 'Auth API',
        failed_auth: 'Failed auth',
        unavailable: 'Admin metrics are temporarily unavailable.',
        range_total: '{{range}} range total',
        completed_orders: '{{count}} completed orders',
        completed_payments_usd: 'USD, completed payments',
        authenticated_activity: 'Authenticated API activity',
        failed_auth_attempts: '{{count}} failed auth attempts',
        dashboard_title: 'Admin Dashboard',
        business_timezone: 'Business timezone: {{timezone}}',
        granularity: 'Granularity: {{granularity}}',
        refresh: 'Refresh',
    },
    fr: {
        thirty_days: '30 jours',
        twenty_weeks: '20 semaines',
        twelve_months: '12 mois',
        new_users: 'Nouveaux utilisateurs',
        paid_users: 'Utilisateurs payants',
        paid_revenue: 'Revenus payés',
        active_users: 'Utilisateurs actifs',
        api_activity: 'Activité API',
        authenticated_api_requests: 'Requêtes API authentifiées',
        trend_overview: 'Vue des tendances',
        trend_description: 'Normalisé par métrique afin de comparer différentes échelles.',
        trend_chart_label: 'Graphique des métriques administrateur',
        payment_channels: 'Canaux de paiement',
        unclassified: 'Non classé',
        orders: 'commandes',
        bucket_details: 'Détails des périodes',
        raw_rows: 'Lignes API brutes en lecture seule.',
        buckets: '{{count}} périodes',
        period: 'Période',
        new: 'Nouveaux',
        paid: 'Utilisateurs payants',
        revenue: 'Revenus',
        active: 'Actifs',
        auth_api: 'API auth.',
        failed_auth: 'Échecs auth.',
        unavailable: 'Les métriques administrateur sont temporairement indisponibles.',
        range_total: 'Total sur {{range}}',
        completed_orders: '{{count}} commandes finalisées',
        completed_payments_usd: 'USD, paiements finalisés',
        authenticated_activity: 'Activité API authentifiée',
        failed_auth_attempts: '{{count}} tentatives auth. échouées',
        dashboard_title: 'Tableau de bord admin',
        business_timezone: 'Fuseau horaire métier : {{timezone}}',
        granularity: 'Granularité : {{granularity}}',
        refresh: 'Actualiser',
    },
    'zh-CHS': {
        thirty_days: '30 天',
        twenty_weeks: '20 周',
        twelve_months: '12 个月',
        new_users: '新增用户',
        paid_users: '付费用户',
        paid_revenue: '付费收入',
        active_users: '活跃用户',
        api_activity: 'API 活动',
        authenticated_api_requests: '认证 API 请求',
        trend_overview: '趋势概览',
        trend_description: '每个指标单独标准化，便于比较不同量级。',
        trend_chart_label: '管理员指标趋势图',
        payment_channels: '支付渠道',
        unclassified: '未分类',
        orders: '订单',
        bucket_details: '周期明细',
        raw_rows: '只读原始 API 数据行。',
        buckets: '{{count}} 个周期',
        period: '周期',
        new: '新增',
        paid: '付费用户',
        revenue: '收入',
        active: '活跃',
        auth_api: '认证 API',
        failed_auth: '认证失败',
        unavailable: '管理员指标暂时不可用。',
        range_total: '{{range}} 区间合计',
        completed_orders: '{{count}} 个已完成订单',
        completed_payments_usd: '美元，已完成支付',
        authenticated_activity: '认证 API 活动',
        failed_auth_attempts: '{{count}} 次认证失败尝试',
        dashboard_title: '管理员仪表盘',
        business_timezone: '业务时区：{{timezone}}',
        granularity: '粒度：{{granularity}}',
        refresh: '刷新',
    },
    'zh-CHZ': {
        thirty_days: '30 天',
        twenty_weeks: '20 週',
        twelve_months: '12 個月',
        new_users: '新增用戶',
        paid_users: '付費用戶',
        paid_revenue: '付費收入',
        active_users: '活躍用戶',
        api_activity: 'API 活動',
        authenticated_api_requests: '認證 API 請求',
        trend_overview: '趨勢概覽',
        trend_description: '每個指標單獨標準化，便於比較不同量級。',
        trend_chart_label: '管理員指標趨勢圖',
        payment_channels: '支付渠道',
        unclassified: '未分類',
        orders: '訂單',
        bucket_details: '週期明細',
        raw_rows: '唯讀原始 API 資料列。',
        buckets: '{{count}} 個週期',
        period: '週期',
        new: '新增',
        paid: '付費用戶',
        revenue: '收入',
        active: '活躍',
        auth_api: '認證 API',
        failed_auth: '認證失敗',
        unavailable: '管理員指標暫時不可用。',
        range_total: '{{range}} 區間合計',
        completed_orders: '{{count}} 個已完成訂單',
        completed_payments_usd: '美元，已完成支付',
        authenticated_activity: '認證 API 活動',
        failed_auth_attempts: '{{count}} 次認證失敗嘗試',
        dashboard_title: '管理員儀表板',
        business_timezone: '業務時區：{{timezone}}',
        granularity: '粒度：{{granularity}}',
        refresh: '重新整理',
    },
    ar: {
        thirty_days: '30 يومًا',
        twenty_weeks: '20 أسبوعًا',
        twelve_months: '12 شهرًا',
        new_users: 'مستخدمون جدد',
        paid_users: 'مستخدمون مدفوعون',
        paid_revenue: 'الإيرادات المدفوعة',
        active_users: 'مستخدمون نشطون',
        api_activity: 'نشاط API',
        authenticated_api_requests: 'طلبات API الموثقة',
        trend_overview: 'نظرة عامة على الاتجاه',
        trend_description: 'يتم تطبيع كل مؤشر حتى يمكن مقارنة المقاييس المختلفة.',
        trend_chart_label: 'مخطط اتجاه مؤشرات الإدارة',
        payment_channels: 'قنوات الدفع',
        unclassified: 'غير مصنف',
        orders: 'طلبات',
        bucket_details: 'تفاصيل الفترات',
        raw_rows: 'صفوف API خام للقراءة فقط.',
        buckets: '{{count}} فترات',
        period: 'الفترة',
        new: 'جدد',
        paid: 'مستخدمون مدفوعون',
        revenue: 'الإيرادات',
        active: 'نشطون',
        auth_api: 'API موثق',
        failed_auth: 'فشل التوثيق',
        unavailable: 'مؤشرات الإدارة غير متاحة مؤقتًا.',
        range_total: 'إجمالي نطاق {{range}}',
        completed_orders: '{{count}} طلبات مكتملة',
        completed_payments_usd: 'بالدولار الأمريكي، مدفوعات مكتملة',
        authenticated_activity: 'نشاط API موثق',
        failed_auth_attempts: '{{count}} محاولات توثيق فاشلة',
        dashboard_title: 'لوحة تحكم الإدارة',
        business_timezone: 'المنطقة الزمنية للأعمال: {{timezone}}',
        granularity: 'درجة التجميع: {{granularity}}',
        refresh: 'تحديث',
    },
};

const arTranslation = {
    common: {
        email: 'البريد الإلكتروني',
        password: 'كلمة المرور',
        confirm_password: 'تأكيد كلمة المرور',
        first_name: 'الاسم الأول',
        last_name: 'اسم العائلة',
        telephone: 'الهاتف',
        optional: 'اختياري',
        send: 'إرسال',
        submit: 'إرسال',
        cancel: 'إلغاء',
        confirm: 'تأكيد',
        back: 'رجوع',
        details: 'التفاصيل',
        question: 'سؤال',
        site_busy_error: 'الموقع مشغول، يرجى المحاولة لاحقًا',
        invalid_token: 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى!',
        coming_soon: 'قريبًا ...',
        reset: 'إعادة ضبط',
        loading: 'جار التحميل...',
        play: 'تشغيل',
        pause: 'إيقاف مؤقت',
        start_date: 'تاريخ البداية',
        end_date: 'تاريخ النهاية',
        scroll_to_top: 'الأعلى',
        show_password: 'إظهار كلمة المرور',
        hide_password: 'إخفاء كلمة المرور',
    },
    checkout: {
        checkout: 'الدفع',
        payment_details: 'تفاصيل الدفع',
        card_number: 'رقم البطاقة',
        expiry: 'تاريخ الانتهاء',
        cvc: 'CVC',
        name_on_card: 'الاسم على البطاقة',
        country: 'الدولة',
        postal_code: 'الرمز البريدي',
        pay_now: 'ادفع الآن',
        order_summary: 'ملخص الطلب',
        days_plan: 'خطة {{days}} يومًا',
        total: 'الإجمالي',
        plan_includes: 'تتضمن الخطة',
        days_access: 'وصول لمدة {{days}} يومًا',
        all_practice_tests: 'جميع اختبارات التدريب',
        writing_speaking_tasks: 'مهام الكتابة والمحادثة',
        no_plan_selected: 'لم يتم اختيار خطة',
        choose_plan_description: 'اختر الخطة الأنسب لمدة استعدادك.',
        full_access: 'وصول كامل',
        includes: 'تشمل',
        writing_tasks_1_3: 'مهام الكتابة 1-3',
        speaking_tasks_2_3: 'مهام المحادثة 2-3',
        progress_tracking: 'تتبع التقدم',
        need_custom_plan: 'تحتاج إلى خطة مخصصة؟',
        custom_plan_description: 'إذا كنت تحتاج إلى مدة مختلفة أو وصول لفريق، تواصل معنا للحصول على خطة مخصصة.',
        payment_method_for_plan: 'طريقة الدفع لخطة {{days}} يومًا',
        you_save: 'توفر {{amount}}',
        create_order_failed: 'تعذر إنشاء الطلب',
        network_error: 'خطأ في الشبكة، يرجى المحاولة مرة أخرى',
        payment_successful_toast_title: 'تم الدفع بنجاح',
        payment_successful_toast_description: 'تم تفعيل اشتراكك.',
        qr_generating: 'جار إنشاء رمز QR...',
        wechat_pay_title: 'دفع WeChat',
        wechat_pay_instruction: 'امسح رمز QR أدناه لإكمال الدفع.',
        wechat_qr_alt: 'رمز QR لدفع WeChat',
        order_id: 'رقم الطلب',
        expires: 'ينتهي',
        waiting_for_payment: 'بانتظار تأكيد الدفع...',
        complete_within_30_minutes: 'يرجى إكمال الدفع خلال 30 دقيقة.',
        back_to_home: 'العودة إلى الرئيسية',
        verifying_payment: 'جار التحقق من الدفع...',
        payment_successful_title: 'تم الدفع بنجاح',
        payment_successful_description: 'شكرًا لك! أصبح اشتراكك نشطًا الآن.',
        go_to_homepage: 'الذهاب إلى الصفحة الرئيسية',
        view_subscription: 'عرض الاشتراك',
        payment_pending_title: 'الدفع قيد المعالجة',
        payment_pending_description: 'تتم معالجة الدفع. يرجى التحديث بعد قليل.',
        refresh: 'تحديث',
        payment_failed_title: 'فشل الدفع',
        payment_failed_description: 'لم نتمكن من تأكيد الدفع. يرجى المحاولة مرة أخرى.',
        try_again: 'حاول مرة أخرى',
        contact_support: 'تواصل مع الدعم',
        processing_payment: 'جار معالجة الدفع',
        processing_payment_description: 'يرجى الانتظار بينما نؤكد نتيجة الدفع.',
        days_full_access: 'يومًا من الوصول الكامل',
        day: 'يوم',
        off: 'خصم',
        discount: 'خصم',
        subscribe: 'اشترك',
        subscription_note: 'لمزيد من المعلومات، يرجى التواصل معنا.',
        contact_us: 'تواصل معنا',
        order_available_within: 'الطلب صالح لمدة',
        scan_wechat_to_pay: 'امسح رمز WeChat QR للدفع',
        confirm_payment: 'تأكيد الدفع',
        order_expired: 'انتهت صلاحية الطلب',
        payment_completed: 'تم الدفع',
        payment_not_completed: 'لم يتم الدفع',
        wechat: 'WeChat',
        credit_card: 'بطاقة ائتمان',
        payment_select: 'يرجى اختيار طريقة دفع',
    },
    checkout_result: {
        go_to_homepage: 'الذهاب إلى الصفحة الرئيسية',
        go_to_subscription: 'الذهاب إلى الاشتراك',
        subscription_succeed: 'تم تأكيد الاشتراك!',
        subscription_failed: 'فشل الاشتراك!',
    },
    header: {
        homepage: 'الرئيسية',
        objective_testing: 'الفهم',
        listening_comprehension: 'الاستماع',
        reading_comprehension: 'القراءة',
        subjective_testing: 'التواصل',
        speaking: 'المحادثة',
        writing: 'الكتابة',
        task: 'المهمة',
        Subscription: 'الاشتراك',
        my_profile: 'ملفي الشخصي',
    },
    login: {
        sign_up: 'إنشاء حساب',
        login: 'تسجيل الدخول',
        login_field_missing: 'البريد الإلكتروني أو كلمة المرور مفقودان',
        login_failed_mismatch: 'فشل تسجيل الدخول، يرجى التحقق من البريد الإلكتروني وكلمة المرور',
        login_failed_server: 'فشل تسجيل الدخول، يرجى المحاولة لاحقًا',
        logout: 'تسجيل الخروج',
        welcome_back: 'مرحبًا بعودتك',
        login_intro: 'سجّل الدخول لمتابعة استعدادك لاختبار TCF Canada',
        no_account: 'ليس لديك حساب؟',
        terms_notice: 'بتسجيل الدخول، فإنك توافق على شروط الخدمة وسياسة الخصوصية.',
    },
    register: {
        email_verification_code: 'رمز التحقق عبر البريد الإلكتروني',
        send_verification_code: 'إرسال رمز التحقق',
        enter_CAPTCHA: 'أدخل رمز CAPTCHA',
        register_field_missing: 'البريد الإلكتروني وكلمة المرور والاسم الأول واسم العائلة ورمز CAPTCHA مطلوبة',
        username_length_invalid: 'يجب أن يكون اسم المستخدم بين 4 و30 حرفًا',
        password_length_invalid: 'يجب أن تكون كلمة المرور بين 8 و30 حرفًا',
        password_not_match: 'كلمات المرور غير متطابقة',
        register_succeed: 'تم التسجيل بنجاح!',
        register_failed_later: 'فشل التسجيل، يرجى المحاولة لاحقًا',
        back_to_login: 'العودة إلى الصفحة السابقة',
        create_account: 'إنشاء حسابك',
        start_journey: 'ابدأ رحلة الاستعداد لـ TCF Canada اليوم',
        redirect_to_login: 'سيتم تحويلك إلى تسجيل الدخول خلال 5 ثوانٍ...',
        already_account: 'لديك حساب بالفعل؟',
        terms_notice: 'بإنشاء حساب، فإنك توافق على شروط الخدمة وسياسة الخصوصية.',
    },
    homepage: {
        heading: 'Master Key for TCF Canada',
        slogan: 'الحل الشامل للنجاح فيه',
        info_heading: 'حول TCF Canada',
        info_slogan: 'كل ما تحتاج إلى معرفته عنه',
        writing_topic: 'أحدث موضوعات الكتابة',
        speaking_topic: 'أحدث موضوعات المحادثة',
        insight: 'رؤى',
        test_center: 'مراكز الاختبار',
        official_link: 'روابط رسمية',
        learn_more: 'معرفة المزيد',
        patch_date: 'تحديث جديد 4/21',
        patch_note_1: '> قمنا بتحديث التحليل باستخدام Claude Sonnet 3.7، وأصبح أفضل من أي وقت مضى!',
        patch_note_2: '> لدى Master Key الآن موضوعات محادثة وكتابة سابقة ومحدثة!',
        patch_note_3: '> أصلحنا بعض المشكلات البسيطة بناءً على ملاحظات المستخدمين.',
        masterkey_title: 'Master Key for TCF Canada',
        function_description_1: 'اختبارات استماع وقراءة حقيقية من مراكز اختبار عالمية مع وضعي الاختبار والتدريب',
        function_description_2: 'نصوص الاستماع وتحليل الأسئلة مولدة بأفضل نماذج الذكاء الاصطناعي',
        function_description_3: 'ملاحظات دراسة لجمع الأسئلة والموضوعات لمراجعتها لاحقًا',
        function_description_4: 'أداة مدعومة بالذكاء الاصطناعي لإنشاء موضوعات الكتابة والمحادثة الخاصة بك',
    },
    profile: {
        no_plan: 'لا توجد خطة',
        membership_intro: 'سيكون وصولك الكامل متاحًا حتى',
        extend_membership: 'تمديد العضوية',
        update_password: 'تحديث كلمة المرور',
        current_password: 'كلمة المرور الحالية',
        new_password: 'كلمة مرور جديدة',
        confirm_new_password: 'تأكيد كلمة المرور الجديدة',
        account: 'الحساب',
        check_subscription: 'عرض خطط الاشتراك',
        non_membership_header: 'اشترك كعضو كامل للوصول إلى الموارد التالية',
        membership_header: 'أنت عضو كامل ولديك وصول إلى الموارد التالية',
        membership_right_1: '36 اختبار استماع حقيقي لـ TCF Canada + تدريب غير محدود بالذكاء الاصطناعي',
        membership_right_2: '37 اختبار قراءة حقيقي لـ TCF Canada + تدريب غير محدود بالذكاء الاصطناعي',
        membership_right_3: 'موضوعات محادثة محدثة من اختبارات TCF حقيقية + تقييم وتدريب بالذكاء الاصطناعي',
        membership_right_4: 'موضوعات كتابة محدثة من اختبارات TCF حقيقية + تقييم وتدريب بالذكاء الاصطناعي',
        change_password_field_missing: 'هناك حقول مطلوبة مفقودة',
        password_not_match: 'كلمة المرور غير متطابقة',
        change_password_failed: 'فشل تغيير كلمة المرور',
    },
    comprehension: {
        comprehension_test_structure: 'بنية اختبار الفهم',
        become_member_intro: 'يجب أن تصبح عضوًا كاملًا للوصول إلى جميع الاختبارات الحقيقية.',
        become_member_button: 'اشترك',
        listening_test_label_prefix: 'CO - اختبار ',
        reading_test_label_prefix: 'CE - اختبار ',
        test: 'اختبار',
        train: 'تدريب',
    },
    test_question: {
        previous_question: 'السؤال السابق',
        next_question: 'السؤال التالي',
        transcript: 'النص',
        breakdown: 'التفصيل',
        show_transcript: 'إظهار النص',
        show_breakdown: 'إظهار التفصيل',
        original_text: 'النص الأصلي',
        translation: 'الترجمة',
        key_vocabulary_and_expressions: 'المفردات والتعابير الأساسية',
        reference_answer: 'الإجابة المرجعية',
        show_analysis: 'إظهار التحليل',
        summary: 'ملخص',
        translation_analysis: 'الترجمة والتحليل',
        options: 'الخيارات',
        explanation: 'الشرح',
        conclusion: 'الخلاصة',
        finish: 'إنهاء',
        confirm_finish_test: 'هل تؤكد إنهاء الاختبار؟',
    },
    test_result: {
        test_result: 'نتيجة الاختبار',
        correct: 'صحيح',
        wrong: 'خطأ',
        answered: 'تمت الإجابة',
        not_answered: 'لم تتم الإجابة',
        total_score: 'الدرجة الإجمالية',
        score: 'الدرجة',
        correct_answer: 'الإجابة الصحيحة',
        your_answer: 'إجابتك',
        under_levels: 'أقل من 4',
    },
    subjective_listing: {
        no_tags: 'لا توجد وسوم',
        no_summary: 'الملخص غير متوفر',
        failed_load_data: 'فشل تحميل البيانات',
        membership_required: 'العضوية مطلوبة',
        upgrade_membership_prompt: 'يرجى ترقية عضويتك للوصول إلى هذه المهمة.',
        loading: 'جار التحميل...',
        become_member_all_tasks: 'يجب أن تصبح عضوًا كاملًا للوصول إلى جميع مهام المحادثة.',
        completed: 'مكتمل',
        no_content: 'لا يوجد محتوى متاح',
    },
    subjective_detail: {
        writing: 'الكتابة',
        speaking: 'المحادثة',
        subject: 'الموضوع',
        word_limitation: '(120 كلمة كحد أدنى / 150 كلمة كحد أقصى)',
        writing_task_1_word_limitation: '(60 كلمة كحد أدنى / 120 كلمة كحد أقصى)',
        writing_task_2_word_limitation: '(120 كلمة كحد أدنى / 150 كلمة كحد أقصى)',
        writing_task_3_word_limitation: '(120 كلمة كحد أدنى / 180 كلمة كحد أقصى)',
        speaking_task_2_word_limitation: '(3 دقائق و30 ثانية من التفاعل)',
        speaking_task_3_word_limitation: '(4 دقائق و30 ثانية)',
        AI_release_soon: 'الكتابة المدعومة بالذكاء الاصطناعي ستتوفر قريبًا.',
        failed_load_question: 'فشل تحميل السؤال',
        enter_answer: 'يرجى إدخال إجابتك',
        answer_submitted_success: 'تم إرسال الإجابة بنجاح',
        failed_submit_answer: 'فشل إرسال الإجابة',
        loading_question: 'جار تحميل السؤال...',
        question_label: 'سؤال',
        word_limit: 'حد الكلمات: {{wordLimit}}',
        custom_word_limitation: '({{min}} كلمة كحد أدنى / {{max}} كلمة كحد أقصى)',
        document_1: 'المستند 1 :',
        document_2: 'المستند 2 :',
        your_answer: 'إجابتك',
        writing_placeholder: 'اكتب إجابتك هنا...',
        speaking_placeholder: 'اكتب مسودة المحادثة هنا...',
        submitting: 'جار الإرسال...',
        submit_answer: 'إرسال الإجابة',
    },
    pagination: {
        go_to_page: 'الانتقال إلى الصفحة',
    },
    footer: {
        platform_intro: 'منصة تحضير لاختبار TCF Canada لكفاءة اللغتين الإنجليزية والفرنسية.',
        contact_us: 'تواصل معنا',
        faq: 'الأسئلة الشائعة',
        faq_link_1: 'مركز المساعدة',
        faq_link_2: 'الدعم',
        social_media: 'وسائل التواصل',
        follow_us: 'تابعنا',
        red_note: 'Red Note',
        wechat_label: 'WeChat',
        all_rights_reserved: 'جميع الحقوق محفوظة.',
    },
    admin: adminTranslations.ar,
};

i18n
    // detect user language
    // learn more: https://github.com/i18next/i18next-browser-languageDetector
    // .use(LanguageDetector)
    // pass the i18n instance to react-i18next.
    .use(initReactI18next)
    // init i18next
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
        debug: true,
        fallbackLng: 'en',
        lng: getSavedLanguage(),
        interpolation: {
            escapeValue: false, // not needed for react as it escapes by default
        },
        resources: {
            en: {
                translation: {
                    common: {
                        email: 'Email',
                        password: 'Password',
                        confirm_password: 'Confirm password',
                        first_name: 'First name',
                        last_name: 'Last name',
                        telephone: 'Phone',
                        optional: 'Optional',
                        send: 'Send',
                        submit: 'Submit',
                        cancel: 'Cancel',
                        confirm: 'Confirm',
                        back: 'Back',
                        details: 'Details',
                        question: 'Question',
                        site_busy_error: 'The site is busy, please try again later',
                        invalid_token: 'Token expired, please login again!',
                        coming_soon: 'Coming soon ...',
                        reset: 'Reset',
                        loading: 'Loading...',
                        play: 'Play',
                        pause: 'Pause',
                        start_date: 'Start Date',
                        end_date: 'End Date',
                        scroll_to_top: 'Top',
                        show_password: 'Show password',
                        hide_password: 'Hide password',
                    },
                    checkout: {
                        checkout: 'Checkout',
                        payment_details: 'Payment Details',
                        card_number: 'Card Number',
                        expiry: 'Expiry',
                        cvc: 'CVC',
                        name_on_card: 'Name on Card',
                        country: 'Country',
                        postal_code: 'Postal Code',
                        pay_now: 'Pay Now',
                        order_summary: 'Order Summary',
                        days_plan: '{{days}} Days Plan',
                        total: 'Total',
                        plan_includes: 'Plan includes',
                        days_access: '{{days}} days access',
                        all_practice_tests: 'All practice tests',
                        writing_speaking_tasks: 'Writing and speaking tasks',
                        no_plan_selected: 'No plan selected',
                        choose_plan_description: 'Choose the plan that best fits your preparation timeline.',
                        full_access: 'Full access',
                        includes: 'Includes',
                        writing_tasks_1_3: 'Writing tasks 1-3',
                        speaking_tasks_2_3: 'Speaking tasks 2-3',
                        progress_tracking: 'Progress tracking',
                        need_custom_plan: 'Need a custom plan?',
                        custom_plan_description: 'If you need a different duration or team access, contact us for a custom subscription plan.',
                        payment_method_for_plan: 'Payment method for {{days}}-day plan',
                        you_save: 'You save {{amount}}',
                        create_order_failed: 'Failed to create order',
                        network_error: 'Network error, please try again',
                        payment_successful_toast_title: 'Payment successful',
                        payment_successful_toast_description: 'Your subscription has been activated.',
                        qr_generating: 'Generating QR code...',
                        wechat_pay_title: 'WeChat Pay',
                        wechat_pay_instruction: 'Scan the QR code below to complete your payment.',
                        wechat_qr_alt: 'WeChat payment QR code',
                        order_id: 'Order ID',
                        expires: 'Expires',
                        waiting_for_payment: 'Waiting for payment confirmation...',
                        complete_within_30_minutes: 'Please complete payment within 30 minutes.',
                        back_to_home: 'Back to home',
                        verifying_payment: 'Verifying your payment...',
                        payment_successful_title: 'Payment successful',
                        payment_successful_description: 'Thank you! Your subscription is now active.',
                        go_to_homepage: 'Go to homepage',
                        view_subscription: 'View subscription',
                        payment_pending_title: 'Payment pending',
                        payment_pending_description: 'Your payment is being processed. Please refresh in a moment.',
                        refresh: 'Refresh',
                        payment_failed_title: 'Payment failed',
                        payment_failed_description: 'We could not confirm your payment. Please try again.',
                        try_again: 'Try again',
                        contact_support: 'Contact support',
                        processing_payment: 'Processing payment',
                        processing_payment_description: 'Please wait while we confirm your payment result.',
                        days_full_access: 'days full access',
                        day: 'day',
                        off: 'off',
                        discount: 'discount',
                        subscribe: 'Subscribe',
                        subscription_note: 'For more information, please contact us.',
                        contact_us: 'Contact Us',
                        order_available_within: "Order valid for",
                        scan_wechat_to_pay: "Scan WeChat QR code to pay",
                        confirm_payment: "Confirm payment",
                        order_expired: "Order expired",
                        payment_completed: "Payment completed",
                        payment_not_completed: "Payment not completed",
                        wechat: 'WeChat',
                        credit_card: 'Credit Card',
                        payment_select: 'Please select a payment method',
                    },
                    checkout_result: {
                        go_to_homepage: 'Go to Homepage',
                        go_to_subscription: 'Go to Subscription',
                        subscription_succeed: 'Subscription Confirmed!',
                        subscription_failed: 'Subscription Failed!',
                    },
                    header: {
                        homepage: 'Home',
                        objective_testing: 'Comprehension',
                        listening_comprehension: 'Listening',
                        reading_comprehension: 'Reading',
                        subjective_testing: 'Communication',
                        speaking: 'Speaking',
                        writing: 'Writing',
                        task: 'Task',
                        Subscription: 'Subscription',
                        my_profile: "My Profile",
                    },
                    login: {
                        sign_up: 'Sign up',
                        login: 'Login',
                        login_field_missing: 'Email or password is missing',
                        login_failed_mismatch: 'Login failed, please check your username and password',
                        login_failed_server: 'Login failed, please try again later',
                        logout: 'Logout',
                        welcome_back: 'Welcome back',
                        login_intro: 'Sign in to continue your TCF Canada preparation',
                        no_account: "Don't have an account?",
                        terms_notice: 'By signing in, you agree to our Terms of Service and Privacy Policy.',
                    },
                    register: {
                        email_verification_code: "Email verification code",
                        send_verification_code: "Send verification code",
                        enter_CAPTCHA: "Enter CAPTCHA",
                        register_field_missing: 'Email, password, first name, last name, and CAPTCHA code are required',
                        username_length_invalid: 'Username must be between 4 and 30 characters',
                        password_length_invalid: 'Password must be between 8 and 30 characters',
                        password_not_match: "Password doesn't match",
                        register_succeed: "You have registered successfully!",
                        register_failed_later: "Failed to register, please try again later",
                        back_to_login: "Back to previous page",
                        create_account: 'Create your account',
                        start_journey: 'Start your TCF Canada preparation journey today',
                        redirect_to_login: 'You will be redirected to login in 5 seconds...',
                        already_account: 'Already have an account?',
                        terms_notice: 'By creating an account, you agree to our Terms of Service and Privacy Policy.',
                    },
                    homepage: {
                        heading: "Master Key for TCF Canada",
                        slogan: "The one-stop solution to ace it",
                        info_heading: "About TCF Canada",
                        info_slogan: "Everything you need to know about it",
                        writing_topic: 'Recent Writing Topics',
                        speaking_topic: 'Recent Speaking Topics',
                        insight: 'Insights',
                        test_center: 'Test Centers',
                        official_link: 'Official Links',
                        learn_more: 'Learn more',
                        patch_date: '4/21 New Update',
                        patch_note_1: '> We have updated our analysis using Claude Sonnet 3.7, it’s now better than ever before!',
                        patch_note_2: '> Master Key now has history speaking and writing topics, up to date!',
                        patch_note_3: '> We have fixed some minor issues based on user’s feedback.',
                        masterkey_title: 'Master Key for TCF Canada',
                        function_description_1: 'Real listening and reading tests from global testing centers with testing and training modes',
                        function_description_2: 'Listening transcript and question analysis generated by best AI models',
                        function_description_3: 'Study note to collect questions and topics for later review',
                        function_description_4: 'AI-assisted tool to generate your own writing and speaking topics',
                    },
                    profile: {
                        no_plan: "Don't have a plan",
                        membership_intro: "Your full access will be available until",
                        extend_membership: "Extend membership",
                        update_password: "Update password",
                        current_password: 'Current password',
                        new_password: 'New password',
                        confirm_new_password: 'Confirm new password',
                        account: "Account",
                        check_subscription: "Check subscription plans",
                        non_membership_header: "Become a full member to access to the following resources",
                        membership_header: "You are a full member, with access to the following resources",
                        membership_right_1: "36 real TCF Canada listening tests + unlimited AI tutoring",
                        membership_right_2: "37 real TCF Canada reading tests + unlimited AI tutoring",
                        membership_right_3: "Up-to-date speaking topics from real TCF exams + AI assessment and tutoring",
                        membership_right_4: "Up-to-date writing topics from real TCF exams + AI assessment and tutoring",
                        change_password_field_missing: "Required fields are missing",
                        password_not_match: "Password doesn't match",
                        change_password_failed: "Failed to change password",
                    },
                    comprehension: {
                        comprehension_test_structure: 'Structure of the comprehension test',
                        become_member_intro: "You need to become a full member to access all real tests.",
                        become_member_button: "Become a member",
                        listening_test_label_prefix: "CO - Test ",
                        reading_test_label_prefix: "CE - Test ",
                        test: "Test",
                        train: "Train",
                    },
                    test_question: {
                        previous_question: "Previous question",
                        next_question: "Next question",
                        transcript: "Transcript",
                        breakdown: "Breakdown",
                        show_transcript: "Show Transcript",
                        show_breakdown: "Show Breakdown",
                        original_text: "Original Text",
                        translation: "Translation",
                        key_vocabulary_and_expressions: "Key Vocabulary and Expressions",
                        reference_answer: "Reference Answer",
                        show_analysis: "Show Analysis",
                        summary: "Summary",
                        translation_analysis: "Translation and analysis",
                        options: 'Options',
                        explanation: 'Explanation',
                        conclusion: 'Conclusion',
                        finish: "Finish",
                        confirm_finish_test: "Confirm to finish the test?",
                    },
                    test_result: {
                        test_result: "Test result",
                        correct: "Correct",
                        wrong: "Wrong",
                        answered: "Answered",
                        not_answered: "Not answered",
                        total_score: "Total score",
                        score: "Score",
                        correct_answer: "Correct answer",
                        your_answer: "Your answer",
                        under_levels: "Under 4"
                    },
                    subjective_listing: {
                        no_tags: "No tags",
                        no_summary: "Summary not available",
                        failed_load_data: 'Failed to load data',
                        membership_required: 'Membership required',
                        upgrade_membership_prompt: 'Please upgrade your membership to access this task.',
                        loading: 'Loading...',
                        become_member_all_tasks: 'You need to become a full member to access all speaking tasks.',
                        completed: 'Completed',
                        no_content: 'No content available',
                    },
                    subjective_detail: {
                        writing: "Writing",
                        speaking: "Speaking",
                        subject: "Topic",
                        word_limitation: "(120 words minimum / 150 words maximum)",
                        writing_task_1_word_limitation: "(60 words minimum / 120 words maximum)",
                        writing_task_2_word_limitation: "(120 words minimum / 150 words maximum)",
                        writing_task_3_word_limitation: "(120 words minimum / 180 words maximum)",
                        speaking_task_2_word_limitation: "(3 min 30 seconds of interaction)",
                        speaking_task_3_word_limitation: "(4 min 30 seconds)",
                        AI_release_soon: "AI-assisted writing is coming soon.",
                        failed_load_question: 'Failed to load question',
                        enter_answer: 'Please enter your answer',
                        answer_submitted_success: 'Answer submitted successfully',
                        failed_submit_answer: 'Failed to submit answer',
                        loading_question: 'Loading question...',
                        question_label: 'Question',
                        word_limit: 'Word limit: {{wordLimit}}',
                        custom_word_limitation: '({{min}} words minimum / {{max}} words maximum)',
                        document_1: 'Document 1 :',
                        document_2: 'Document 2 :',
                        your_answer: 'Your Answer',
                        writing_placeholder: 'Write your answer here...',
                        speaking_placeholder: 'Write your speaking draft here...',
                        submitting: 'Submitting...',
                        submit_answer: 'Submit Answer',
                    },
                    pagination: {
                        go_to_page: "Go to page",
                    },
                    footer: {
                        platform_intro: 'TCF Canada preparation platform for English and French language proficiency testing.',
                        contact_us: 'Contact Us',
                        faq: 'FAQ',
                        faq_link_1: 'Help Center',
                        faq_link_2: 'Support',
                        social_media: 'Social Media',
                        follow_us: 'FOLLOW US',
                        red_note: 'Red Note',
                        wechat_label: 'WeChat',
                        all_rights_reserved: 'All rights reserved.',
                    },
                    admin: adminTranslations.en,
                }
            },
            fr: {
                translation: {
                    common: {
                        email: 'Courriel',
                        password: 'Mot de Passe',
                        confirm_password: 'Confirmez mot de passe',
                        first_name: 'Prénom',
                        last_name: 'Nom',
                        telephone: 'Téléphone',
                        optional: 'facultatif',
                        send: 'Envoyer',
                        submit: 'S’inscrire',
                        cancel: 'Annuler',
                        confirm: 'Confirmer',
                        back: 'Retour',
                        details: 'Détails',
                        question: 'Question',
                        site_busy_error: 'Le site est occupé, veuillez réessayer plus tard',
                        invalid_token: 'Token expiré, veuillez vous reconnecter!',
                        coming_soon: 'Bientôt disponible ...',
                        reset: 'Réinitialiser',
                        loading: 'Chargement...',
                        play: 'Lire',
                        pause: 'Pause',
                        start_date: 'Date de début',
                        end_date: 'Date de fin',
                        scroll_to_top: 'Haut',
                        show_password: 'Afficher le mot de passe',
                        hide_password: 'Masquer le mot de passe',
                    },
                    header: {
                        homepage: 'Accueil',
                        objective_testing: 'Compréhension',
                        listening_comprehension: 'Compréhension Orale',
                        reading_comprehension: 'Compréhension Écrite',
                        subjective_testing: 'Expression',
                        speaking: 'Expression Orale',
                        writing: 'Expression Écrite',
                        task: 'Tâche',
                        Subscription: 'Abonnement',
                        my_profile: "Mon profil",
                    },
                    checkout: {
                        checkout: 'Paiement',
                        payment_details: 'Details du paiement',
                        card_number: 'Numero de carte',
                        expiry: 'Expiration',
                        cvc: 'CVC',
                        name_on_card: 'Nom sur la carte',
                        country: 'Pays',
                        postal_code: 'Code postal',
                        pay_now: 'Payer maintenant',
                        order_summary: 'Resume de la commande',
                        days_plan: 'Forfait de {{days}} jours',
                        total: 'Total',
                        plan_includes: 'Le forfait comprend',
                        days_access: 'Acces de {{days}} jours',
                        all_practice_tests: 'Tous les tests de pratique',
                        writing_speaking_tasks: 'Taches d\'expression ecrite et orale',
                        no_plan_selected: 'Aucun forfait selectionne',
                        choose_plan_description: 'Choisissez le forfait qui convient le mieux a votre preparation.',
                        full_access: 'Acces complet',
                        includes: 'Comprend',
                        writing_tasks_1_3: 'Taches d\'ecriture 1-3',
                        speaking_tasks_2_3: 'Taches orales 2-3',
                        progress_tracking: 'Suivi des progres',
                        need_custom_plan: 'Besoin d\'un forfait personnalise ?',
                        custom_plan_description: 'Si vous avez besoin d\'une duree differente ou d\'un acces equipe, contactez-nous pour un forfait personnalise.',
                        payment_method_for_plan: 'Mode de paiement pour le forfait {{days}} jours',
                        you_save: 'Vous economisez {{amount}}',
                        create_order_failed: 'Echec de creation de la commande',
                        network_error: 'Erreur reseau, veuillez reessayer',
                        payment_successful_toast_title: 'Paiement reussi',
                        payment_successful_toast_description: 'Votre abonnement est maintenant actif.',
                        qr_generating: 'Generation du code QR...',
                        wechat_pay_title: 'Paiement WeChat',
                        wechat_pay_instruction: 'Scannez le code QR ci-dessous pour terminer le paiement.',
                        wechat_qr_alt: 'Code QR de paiement WeChat',
                        order_id: 'Numero de commande',
                        expires: 'Expire le',
                        waiting_for_payment: 'En attente de confirmation du paiement...',
                        complete_within_30_minutes: 'Veuillez finaliser le paiement dans les 30 minutes.',
                        back_to_home: 'Retour a l\'accueil',
                        verifying_payment: 'Verification du paiement...',
                        payment_successful_title: 'Paiement reussi',
                        payment_successful_description: 'Merci! Votre abonnement est actif.',
                        go_to_homepage: 'Aller a la page d\'accueil',
                        view_subscription: 'Voir l\'abonnement',
                        payment_pending_title: 'Paiement en attente',
                        payment_pending_description: 'Votre paiement est en cours de traitement. Veuillez actualiser dans un instant.',
                        refresh: 'Actualiser',
                        payment_failed_title: 'Paiement echoue',
                        payment_failed_description: 'Nous n\'avons pas pu confirmer votre paiement. Veuillez reessayer.',
                        try_again: 'Reessayer',
                        contact_support: 'Contacter le support',
                        processing_payment: 'Traitement du paiement',
                        processing_payment_description: 'Veuillez patienter pendant la confirmation du paiement.',
                        days_full_access: 'jours d\'accès complet',
                        day: 'jour',
                        off: 'de réduction',
                        discount: 'réduction',
                        subscribe: 'S\'abonner',
                        subscription_note: 'Pour plus d\'informations, veuillez nous contacter.',
                        contact_us: 'Contactez-nous',
                        order_available_within: "Commande valable pendant",
                        scan_wechat_to_pay: "Scannez le code QR WeChat pour payer",
                        confirm_payment: "Confirmer le paiement",
                        order_expired: "Commande expirée",
                        payment_completed: "Paiement effectué",
                        payment_not_completed: "Paiement non effectué",
                        wechat: 'WeChat',
                        credit_card: 'Carte de crédit',
                        payment_select: 'Veuillez sélectionner un mode de paiement',
                    },
                    checkout_result: {
                        go_to_homepage: 'Aller à la page d\'accueil',
                        go_to_subscription: 'Aller à l\'abonnement',
                        subscription_succeed: 'Abonnement confirmé!',
                        subscription_failed: 'Échec de l\'abonnement!',
                    },
                    login: {
                        sign_up: 'S\'inscrire',
                        login: 'Se connecter',
                        login_field_missing: 'L\'email ou le mot de passe est manquant',
                        login_failed_mismatch: 'Échec de la connexion, veuillez vérifier votre nom d\'utilisateur et votre mot de passe',
                        login_failed_server: 'Échec de la connexion, veuillez réessayer plus tard',
                        logout: 'Se déconnecter',
                        welcome_back: 'Bon retour',
                        login_intro: 'Connectez-vous pour continuer votre préparation au TCF Canada',
                        no_account: "Vous n'avez pas de compte ?",
                        terms_notice: 'En vous connectant, vous acceptez nos conditions d’utilisation et notre politique de confidentialité.',
                    },
                    register: {
                        email_verification_code: "Code de vérification du courriel",
                        send_verification_code: "Envoyer le code de vérification",
                        enter_CAPTCHA: "Entrez le CAPTCHA",
                        register_field_missing: 'L\'adresse électronique, le mot de passe, le prénom, le nom de famille et le code CAPTCHA sont obligatoires.',
                        username_length_invalid: 'Le nom d\'utilisateur doit comporter entre 4 et 30 caractères',
                        password_length_invalid: 'Le mot de passe doit comporter entre 8 et 30 caractères',
                        password_not_match: "Le mot de passe ne correspond pas",
                        register_succeed: "Vous vous êtes bien inscrit(e) !",
                        register_failed_later: "Échec de l'inscription, veuillez réessayer plus tard",
                        back_to_login: "Retourner à la page précédente",
                        create_account: 'Créer votre compte',
                        start_journey: 'Commencez votre préparation au TCF Canada aujourd’hui',
                        redirect_to_login: 'Vous serez redirigé vers la connexion dans 5 secondes...',
                        already_account: 'Vous avez déjà un compte ?',
                        terms_notice: 'En créant un compte, vous acceptez nos conditions d’utilisation et notre politique de confidentialité.',
                    },
                    homepage: {
                        heading: "Master Key for TCF Canada",
                        slogan: "La solution ultime pour réussir",
                        info_heading: "À propos du TCF Canada",
                        info_slogan: "Tout ce que vous devez savoir à ce sujet",
                        writing_topic: 'Sujets d\'écriture récents',
                        speaking_topic: 'Sujets de conversation récents',
                        insight: 'Aperçu',
                        test_center: 'Centres d\'examen',
                        official_link: 'Liens officiels',
                        learn_more: 'En savoir plus',
                        my_profile: "Mon profil",
                        patch_date: 'Mise à jour du 21/04',
                        patch_note_1: '> Nous avons mis à jour notre analyse en utilisant Claude Sonnet 3.7, c\'est maintenant mieux que jamais !',
                        patch_note_2: '> Master Key a maintenant des sujets d\'expression orale et écrite historiques, à jour !',
                        patch_note_3: '> Nous avons corrigé quelques problèmes mineurs en fonction des commentaires des utilisateurs.',
                        masterkey_title: 'Master Key for TCF Canada',
                        function_description_1: 'Tests d\'écoute et de lecture réels des centres de test mondiaux avec modes de test et d\'entraînement',
                        function_description_2: 'Transcription d\'écoute et analyse des questions générées par les meilleurs modèles d\'IA',
                        function_description_3: 'Note d\'étude pour collecter des questions et des sujets pour une révision ultérieure',
                        function_description_4: 'Outil assisté par IA pour générer vos propres sujets d\'expression écrite et orale',
                    },
                    profile: {
                        no_plan: "Ne pas avoir de plan",
                        membership_intro: "Votre accès complet sera disponible jusqu'à",
                        extend_membership: "Prolonger l'adhésion",
                        update_password: "Mettre à jour le mot de passe",
                        current_password: 'Mot de passe actuel',
                        new_password: 'Nouveau mot de passe',
                        confirm_new_password: 'Confirmer le nouveau mot de passe',
                        account: "Compte",
                        check_subscription: "Vérifier les plans d'abonnement",
                        non_membership_header: "Devenez un membre à part entière pour accéder aux ressources suivantes",
                        membership_header: "Vous êtes membre à part entière et avez accès aux ressources suivantes",
                        membership_right_1: "36 tests d'écoute réels du TCF Canada + tutorat AI illimité",
                        membership_right_2: "37 vrais tests de lecture du TCF Canada + tutorat AI illimité",
                        membership_right_3: "Sujets d'expression orale actualisés tirés d'examens réels du TCF + évaluation et tutorat AI",
                        membership_right_4: "Sujets d'écriture actualisés tirés d'examens TCF réels + évaluation et tutorat AI",
                        change_password_field_missing: "Les champs obligatoires sont manquants",
                        password_not_match: "Le mot de passe ne correspond pas",
                        change_password_failed: "Échec du changement de mot de passe",
                    },
                    comprehension: {
                        comprehension_test_structure: 'Structure du test de compréhension',
                        become_member_intro: "Vous devez devenir membre à part entière pour accéder à tous les vrais tests.",
                        become_member_button: "Devenir membre",
                        listening_test_label_prefix: "CO - Test ",
                        reading_test_label_prefix: "CE - Test ",
                        test: "Test",
                        train: "Train",
                    },
                    test_question: {
                        previous_question: "Question précédente",
                        next_question: "Question suivante",
                        transcript: "Transcription",
                        breakdown: "Détails",
                        show_transcript: "Afficher la transcription",
                        show_breakdown: "Afficher les détails",
                        original_text: "Texte original",
                        translation: "Traduction",
                        key_vocabulary_and_expressions: "Vocabulaire clé et expressions",
                        reference_answer: "Réponse de référence",
                        show_analysis: "Afficher l'analyse",
                        summary: "Résumé",
                        translation_analysis: "Traduction et analyse",
                        options: 'Options',
                        explanation: 'Explication',
                        conclusion: 'Conclusion',
                        finish: "Terminer",
                        confirm_finish_test: "Confirmer pour terminer le test?",
                    },
                    test_result: {
                        test_result: "Résultat du test",
                        correct: "Correct",
                        wrong: "erreur",
                        answered: "Répondu",
                        not_answered: "Non répondu",
                        total_score: "Score total",
                        score: "Score",
                        correct_answer: "Réponse correcte",
                        your_answer: "Votre réponse",
                        under_levels: "Sous 4"
                    },
                    subjective_listing: {
                        no_tags: "Pas de balises",
                        no_summary: "Résumé non disponible",
                        failed_load_data: 'Echec du chargement des donnees',
                        membership_required: 'Abonnement requis',
                        upgrade_membership_prompt: 'Veuillez mettre a niveau votre abonnement pour acceder a cette tache.',
                        loading: 'Chargement...',
                        become_member_all_tasks: 'Vous devez devenir membre complet pour acceder a toutes les taches orales.',
                        completed: 'Termine',
                        no_content: 'Aucun contenu disponible',
                    },
                    subjective_detail: {
                        writing: "Écriture",
                        speaking: "Parler",
                        subject: "Sujet",
                        word_limitation: "(120 mots minimum / 150 mots maximum)",
                        writing_task_1_word_limitation: "(60 mots minimum / 120 mots maximum)",
                        writing_task_2_word_limitation: "(120 mots minimum / 150 mots maximum)",
                        writing_task_3_word_limitation: "(120 mots minimum / 180 mots maximum)",
                        speaking_task_2_word_limitation: "(3 min 30 secondes d'interaction)",
                        speaking_task_3_word_limitation: "(4 min 30 secondes)",
                        AI_release_soon: "L'écriture assistée par IA arrive bientôt.",
                        failed_load_question: 'Echec du chargement de la question',
                        enter_answer: 'Veuillez saisir votre reponse',
                        answer_submitted_success: 'Reponse soumise avec succes',
                        failed_submit_answer: 'Echec de la soumission de la reponse',
                        loading_question: 'Chargement de la question...',
                        question_label: 'Question',
                        word_limit: 'Limite de mots : {{wordLimit}}',
                        custom_word_limitation: '({{min}} mots minimum / {{max}} mots maximum)',
                        document_1: 'Document 1 :',
                        document_2: 'Document 2 :',
                        your_answer: 'Votre reponse',
                        writing_placeholder: 'Ecrivez votre reponse ici...',
                        speaking_placeholder: 'Ecrivez votre brouillon oral ici...',
                        submitting: 'Soumission...',
                        submit_answer: 'Soumettre la reponse',
                    },
                    pagination: {
                        go_to_page: "Aller à la page",
                    },
                    footer: {
                        platform_intro: 'Plateforme de preparation au TCF Canada pour les tests de competence linguistique en anglais et en francais.',
                        contact_us: 'Contactez-nous',
                        faq: 'FAQ',
                        faq_link_1: 'Centre d\'aide',
                        faq_link_2: 'Support',
                        social_media: 'Réseaux sociaux',
                        follow_us: 'SUIVEZ-NOUS',
                        red_note: 'Red Note',
                        wechat_label: 'WeChat',
                        all_rights_reserved: 'Tous droits reserves.',
                    },
                    admin: adminTranslations.fr,
                }
            },
            'zh-CHS': {
                translation: {
                    common: {
                        email: '电子邮件',
                        password: '密码',
                        confirm_password: '确认密码',
                        first_name: '名字',
                        last_name: '姓氏',
                        telephone: '电话',
                        optional: '可选',
                        send: '发送',
                        submit: '提交',
                        cancel: '取消',
                        confirm: '确认',
                        back: '返回',
                        details: '细节',
                        question: '问题',
                        site_busy_error: '网站繁忙，请稍后再试',
                        invalid_token: '令牌失效，请重新登录！',
                        coming_soon: '即将推出 ...',
                        reset: '重置',
                        loading: '加载中...',
                        play: '播放',
                        pause: '暂停',
                        start_date: '开始日期',
                        end_date: '结束日期',
                        scroll_to_top: '顶部',
                        show_password: '显示密码',
                        hide_password: '隐藏密码',
                    },
                    header: {
                        homepage: '主页',
                        objective_testing: '客观测试',
                        listening_comprehension: '听力测试',
                        reading_comprehension: '阅读测试',
                        subjective_testing: '主观测试',
                        speaking: '口语测试',
                        writing: '写作测试',
                        task: 'Task',
                        Subscription: '订阅',
                        my_profile: "我的资料",
                    },
                    checkout: {
                        checkout: '结账',
                        payment_details: '支付信息',
                        card_number: '卡号',
                        expiry: '有效期',
                        cvc: '安全码',
                        name_on_card: '持卡人姓名',
                        country: '国家',
                        postal_code: '邮政编码',
                        pay_now: '立即支付',
                        order_summary: '订单摘要',
                        days_plan: '{{days}} 天套餐',
                        total: '合计',
                        plan_includes: '套餐包含',
                        days_access: '{{days}} 天访问权限',
                        all_practice_tests: '全部练习测试',
                        writing_speaking_tasks: '写作和口语任务',
                        no_plan_selected: '未选择套餐',
                        choose_plan_description: '请选择最适合您备考周期的套餐。',
                        full_access: '完整访问权限',
                        includes: '包含内容',
                        writing_tasks_1_3: '写作任务 1-3',
                        speaking_tasks_2_3: '口语任务 2-3',
                        progress_tracking: '学习进度跟踪',
                        need_custom_plan: '需要定制套餐？',
                        custom_plan_description: '如果您需要其他时长或团队权限，请联系我们定制套餐。',
                        payment_method_for_plan: '{{days}} 天套餐的支付方式',
                        you_save: '您节省了 {{amount}}',
                        create_order_failed: '创建订单失败',
                        network_error: '网络错误，请稍后重试',
                        payment_successful_toast_title: '支付成功',
                        payment_successful_toast_description: '您的订阅已生效。',
                        qr_generating: '正在生成二维码...',
                        wechat_pay_title: '微信支付',
                        wechat_pay_instruction: '请使用微信扫描下方二维码完成支付。',
                        wechat_qr_alt: '微信支付二维码',
                        order_id: '订单号',
                        expires: '过期时间',
                        waiting_for_payment: '正在等待支付确认...',
                        complete_within_30_minutes: '请在 30 分钟内完成支付。',
                        back_to_home: '返回首页',
                        verifying_payment: '正在验证支付结果...',
                        payment_successful_title: '支付成功',
                        payment_successful_description: '感谢您的购买！您的订阅已开通。',
                        go_to_homepage: '前往首页',
                        view_subscription: '查看订阅',
                        payment_pending_title: '支付处理中',
                        payment_pending_description: '您的支付正在处理中，请稍后刷新。',
                        refresh: '刷新',
                        payment_failed_title: '支付失败',
                        payment_failed_description: '未能确认您的支付，请重试。',
                        try_again: '重试',
                        contact_support: '联系客服',
                        processing_payment: '正在处理支付',
                        processing_payment_description: '请稍候，我们正在确认支付结果。',
                        days_full_access: '天完整权益',
                        day: '天',
                        off: '折扣',
                        discount: '折扣',
                        subscribe: '订阅',
                        subscription_note: '有关更多信息，请与我们联系。',
                        contact_us: '联系我们',
                        order_available_within: "订单有效期",
                        scan_wechat_to_pay: "扫描微信二维码支付",
                        confirm_payment: "确认支付",
                        order_expired: "订单已过期",
                        payment_completed: "支付完成",
                        payment_not_completed: "支付未完成",
                        wechat: '微信',
                        credit_card: '信用卡',
                        payment_select: '请选择付款方式',
                    },
                    checkout_result: {
                        go_to_homepage: '转到主页',
                        go_to_subscription: '转到订阅',
                        subscription_succeed: '订阅成功！',
                        subscription_failed: '订阅失败！',
                    },
                    login: {
                        sign_up: '注册',
                        login: '登录',
                        login_field_missing: '缺少电子邮件或密码',
                        login_failed_mismatch: '登录失败，请检查您的用户名和密码',
                        login_failed_server: '登录失败，请稍后再试',
                        logout: '登出',
                        welcome_back: '欢迎回来',
                        login_intro: '登录以继续您的 TCF Canada 备考',
                        no_account: '还没有账号？',
                        terms_notice: '登录即表示您同意我们的服务条款和隐私政策。',
                    },
                    register: {
                        email_verification_code: "电子邮件验证码",
                        send_verification_code: "发送验证码",
                        enter_CAPTCHA: "输入验证码",
                        register_field_missing: '需要电子邮件、密码、名字、姓氏和验证码',
                        username_length_invalid: '用户名必须在4到30个字符之间',
                        password_length_invalid: '密码必须在8到30个字符之间',
                        password_not_match: "密码不匹配",
                        register_succeed: "您已成功注册！",
                        register_failed_later: "注册失败，请稍后再试",
                        back_to_login: "返回登录页面",
                        create_account: '创建账号',
                        start_journey: '立即开始您的 TCF Canada 备考之旅',
                        redirect_to_login: '5 秒后将跳转到登录页面...',
                        already_account: '已有账号？',
                        terms_notice: '创建账号即表示您同意我们的服务条款和隐私政策。',
                    },
                    homepage: {
                        heading: "Master Key for TCF Canada",
                        slogan: "终极备考工具，开启上岸坦途",
                        info_heading: "关于 TCF Canada",
                        info_slogan: "备考入门手册",
                        writing_topic: '最新写作题目',
                        speaking_topic: '最新口语题目',
                        insight: '见解',
                        test_center: '考点信息',
                        official_link: 'IRCC官方链接',
                        learn_more: '了解更多',
                        patch_date: '4/21 新版本更新',
                        patch_note_1: '> 我们使用 Claude Sonnet 3.7 更新了分析，现在解析更准确！',
                        patch_note_2: '> Master Key 现在有历史口语和写作题目，包括最新题库！',
                        patch_note_3: '> 我们根据用户反馈修复了一些小问题。',
                        masterkey_title: 'Master Key for TCF Canada',
                        function_description_1: '全球考点真实听力和阅读测试，包含测试和训练模式',
                        function_description_2: '听力原文和题目解析由最好的 AI 模型生成',
                        function_description_3: '学习笔记收集题目和主题以供后续复习',
                        function_description_4: 'AI 辅助工具生成您自己的写作和口语主题',
                    },
                    profile: {
                        no_plan: "未订阅",
                        membership_intro: "您的完整访问权限将持续到",
                        extend_membership: "延长会员资格",
                        update_password: "更新密码",
                        current_password: '当前密码',
                        new_password: '新密码',
                        confirm_new_password: '确认新密码',
                        account: "账户",
                        check_subscription: "查看订阅页面",
                        non_membership_header: "订阅以访问以下资源",
                        membership_header: "您是会员，可以访问以下资源",
                        membership_right_1: "36个真实的 TCF Canada 听力测试 + 无限 AI 辅导",
                        membership_right_2: "37个真实的 TCF Canada 阅读测试 + 无限 AI 辅导",
                        membership_right_3: "来自真实 TCF 考试的最新口语试题 + AI 评估和辅导",
                        membership_right_4: "来自真实 TCF 考试的最新写作试题 + AI 评估和辅导",
                        change_password_field_missing: "缺少必填字段",
                        password_not_match: "密码不匹配",
                        change_password_failed: "密码更改失败",
                    },
                    comprehension: {
                        comprehension_test_structure: '客观测试题目结构',
                        become_member_intro: "您需要成为会员才能访问所有的试题。",
                        become_member_button: "成为会员",
                        listening_test_label_prefix: "CO - 听力测试 ",
                        reading_test_label_prefix: "CE - 阅读测试 ",
                        test: "测试",
                        train: "练习",
                    },
                    test_question: {
                        previous_question: "上一题",
                        next_question: "下一题",
                        transcript: "原文",
                        breakdown: "详解",
                        show_transcript: "显示原文",
                        show_breakdown: "显示详解",
                        original_text: "原文",
                        translation: "翻译",
                        key_vocabulary_and_expressions: "关键词汇和表达",
                        reference_answer: "参考答案",
                        show_analysis: "显示分析",
                        summary: "总结",
                        translation_analysis: "翻译和分析",
                        options: '选项',
                        explanation: '解释',
                        conclusion: '结论',
                        finish: "完成",
                        confirm_finish_test: "确认完成测试？",
                    },
                    test_result: {
                        test_result: "测试结果",
                        correct: "正确",
                        wrong: "错误",
                        answered: "已回答",
                        not_answered: "未回答",
                        total_score: "总分",
                        score: "得分",
                        correct_answer: "正确答案",
                        your_answer: "您的答案",
                        under_levels: "低于 4"
                    },
                    subjective_listing: {
                        no_tags: "没有标签",
                        no_summary: "暂无总结",
                        failed_load_data: '加载数据失败',
                        membership_required: '需要会员权限',
                        upgrade_membership_prompt: '请升级会员以访问此任务。',
                        loading: '加载中...',
                        become_member_all_tasks: '您需要成为会员才能访问所有口语任务。',
                        completed: '已完成',
                        no_content: '暂无内容',
                    },
                    subjective_detail: {
                        writing: "写作",
                        speaking: "口语",
                        subject: "话题",
                        word_limitation: "(最少120字 / 最多150字)",
                        writing_task_1_word_limitation: "(最少60字 / 最多120字)",
                        writing_task_2_word_limitation: "(最少120字 / 最多150字)",
                        writing_task_3_word_limitation: "(最少120字 / 最多180字)",
                        speaking_task_2_word_limitation: "(3分30秒)",
                        speaking_task_3_word_limitation: "(4分30秒)",
                        AI_release_soon: "AI写作即将推出...",
                        failed_load_question: '加载题目失败',
                        enter_answer: '请输入您的答案',
                        answer_submitted_success: '答案提交成功',
                        failed_submit_answer: '提交答案失败',
                        loading_question: '正在加载题目...',
                        question_label: '题目',
                        word_limit: '字数限制：{{wordLimit}}',
                        custom_word_limitation: '(最少 {{min}} 字 / 最多 {{max}} 字)',
                        document_1: '材料 1：',
                        document_2: '材料 2：',
                        your_answer: '你的答案',
                        writing_placeholder: '请在此输入你的写作答案...',
                        speaking_placeholder: '请在此输入你的口语草稿...',
                        submitting: '提交中...',
                        submit_answer: '提交答案',
                    },
                    pagination: {
                        go_to_page: "跳转到页面",
                    },
                    footer: {
                        platform_intro: 'TCF Canada 英语和法语语言能力测试备考平台。',
                        contact_us: '联系我们',
                        faq: '常见问题',
                        faq_link_1: '帮助中心',
                        faq_link_2: '支持',
                        social_media: '社交媒体',
                        follow_us: '关注我们',
                        red_note: '小红书',
                        wechat_label: '微信',
                        all_rights_reserved: '版权所有。',
                    },
                    admin: adminTranslations['zh-CHS'],
                }
            },
            'zh-CHZ': {
                translation: {
                    common: {
                        email: '電子郵件',
                        password: '密碼',
                        confirm_password: '確認密碼',
                        first_name: '名字',
                        last_name: '姓氏',
                        telephone: '電話',
                        optional: '可選',
                        send: '發送',
                        submit: '提交',
                        cancel: '取消',
                        confirm: '確認',
                        back: '返回',
                        details: '細節',
                        question: '問題',
                        site_busy_error: '網站繁忙，請稍後再試',
                        invalid_token: '令牌失效，請重新登錄！',
                        coming_soon: '即將推出 ...',
                        reset: '重置',
                        loading: '載入中...',
                        play: '播放',
                        pause: '暫停',
                        start_date: '開始日期',
                        end_date: '結束日期',
                        scroll_to_top: '頂部',
                        show_password: '顯示密碼',
                        hide_password: '隱藏密碼',
                    },
                    header: {
                        homepage: '主頁',
                        objective_testing: '客觀測試',
                        listening_comprehension: '聽力測試',
                        reading_comprehension: '閱讀測試',
                        subjective_testing: '主觀測試',
                        speaking: '口語測試',
                        writing: '寫作測試',
                        task: 'Task',
                        Subscription: '訂閱',
                        my_profile: "我的資料",
                    },
                    checkout: {
                        checkout: '結帳',
                        payment_details: '付款資訊',
                        card_number: '卡號',
                        expiry: '有效期限',
                        cvc: '安全碼',
                        name_on_card: '持卡人姓名',
                        country: '國家',
                        postal_code: '郵遞區號',
                        pay_now: '立即付款',
                        order_summary: '訂單摘要',
                        days_plan: '{{days}} 天方案',
                        total: '總計',
                        plan_includes: '方案包含',
                        days_access: '{{days}} 天訪問權限',
                        all_practice_tests: '全部練習測試',
                        writing_speaking_tasks: '寫作與口語題目',
                        no_plan_selected: '未選擇方案',
                        choose_plan_description: '請選擇最適合您備考週期的方案。',
                        full_access: '完整訪問權限',
                        includes: '包含內容',
                        writing_tasks_1_3: '寫作題目 1-3',
                        speaking_tasks_2_3: '口語題目 2-3',
                        progress_tracking: '學習進度追蹤',
                        need_custom_plan: '需要客製方案？',
                        custom_plan_description: '若您需要不同時長或團隊權限，請聯絡我們定制方案。',
                        payment_method_for_plan: '{{days}} 天方案的付款方式',
                        you_save: '您節省了 {{amount}}',
                        create_order_failed: '建立訂單失敗',
                        network_error: '網路錯誤，請稍後再試',
                        payment_successful_toast_title: '付款成功',
                        payment_successful_toast_description: '您的訂閱已生效。',
                        qr_generating: '正在產生 QR 碼...',
                        wechat_pay_title: '微信支付',
                        wechat_pay_instruction: '請使用微信掃描下方 QR 碼完成付款。',
                        wechat_qr_alt: '微信支付 QR 碼',
                        order_id: '訂單編號',
                        expires: '到期時間',
                        waiting_for_payment: '正在等待付款確認...',
                        complete_within_30_minutes: '請在 30 分鐘內完成付款。',
                        back_to_home: '返回首頁',
                        verifying_payment: '正在驗證付款結果...',
                        payment_successful_title: '付款成功',
                        payment_successful_description: '感謝您的購買！您的訂閱已啟用。',
                        go_to_homepage: '前往首頁',
                        view_subscription: '查看訂閱',
                        payment_pending_title: '付款處理中',
                        payment_pending_description: '您的付款正在處理中，請稍後重新整理。',
                        refresh: '重新整理',
                        payment_failed_title: '付款失敗',
                        payment_failed_description: '無法確認您的付款，請重試。',
                        try_again: '再試一次',
                        contact_support: '聯絡客服',
                        processing_payment: '正在處理付款',
                        processing_payment_description: '請稍候，我們正在確認付款結果。',
                        days_full_access: '天完整權益',
                        day: '天',
                        off: '折扣',
                        discount: '折扣',
                        subscribe: '訂閱',
                        subscription_note: '有關更多信息，請與我們聯繫。',
                        contact_us: '聯繫我們',
                        order_available_within: "訂單有效期",
                        scan_wechat_to_pay: "掃描微信二維碼支付",
                        confirm_payment: "確認支付",
                        order_expired: "訂單已過期",
                        payment_completed: "付款完成",
                        payment_not_completed: "付款未完成",
                        wechat: '微信',
                        credit_card: '信用卡',
                        payment_select: '請選擇付款方式',
                    },
                    checkout_result: {
                        go_to_homepage: '轉到主頁',
                        go_to_subscription: '轉到訂閱',
                        subscription_succeed: '訂閱成功！',
                        subscription_failed: '訂閱失敗！',
                    },
                    login: {
                        sign_up: '註冊',
                        login: '登錄',
                        login_field_missing: '缺少電子郵件或密碼',
                        login_failed_mismatch: '登錄失敗，請檢查您的用戶名和密碼',
                        login_failed_server: '登錄失敗，請稍後再試',
                        logout: '登出',
                        welcome_back: '歡迎回來',
                        login_intro: '登入以繼續您的 TCF Canada 備考',
                        no_account: '還沒有帳號？',
                        terms_notice: '登入即表示您同意我們的服務條款和隱私政策。',
                    },
                    register: {
                        email_verification_code: "電子郵件驗證碼",
                        send_verification_code: "發送驗證碼",
                        enter_CAPTCHA: "輸入驗證碼",
                        register_field_missing: '需要電子郵件、密碼、名字、姓氏和驗證碼',
                        username_length_invalid: '用戶名必須在4到30個字符之間',
                        password_length_invalid: '密碼必須在8到30個字符之間',
                        password_not_match: "密碼不匹配",
                        register_succeed: "您已成功註冊！",
                        register_failed_later: "註冊失敗，請稍後再試",
                        back_to_login: "返回登錄頁面",
                        create_account: '建立帳號',
                        start_journey: '立即開始您的 TCF Canada 備考之旅',
                        redirect_to_login: '5 秒後將跳轉到登入頁面...',
                        already_account: '已有帳號？',
                        terms_notice: '建立帳號即表示您同意我們的服務條款和隱私政策。',
                    },
                    homepage: {
                        heading: "Master Key for TCF Canada",
                        slogan: "終極備考工具，開啟上岸坦途",
                        info_heading: "關於 TCF Canada",
                        info_slogan: "您需要了解的一切",
                        writing_topic: '最新寫作題目',
                        speaking_topic: '最新口語題目',
                        insight: '見解',
                        test_center: '考試中心',
                        official_link: '官方鏈接',
                        learn_more: '了解更多',
                        patch_date: '4/21 新版本更新',
                        patch_note_1: '> 我們使用 Claude Sonnet 3.7 更新了分析，現在解析更準確！',
                        patch_note_2: '> Master Key 現在有歷史口語和寫作題目，包括最新題庫！',
                        patch_note_3: '> 我們根據用戶反饋修復了一些小問題。',
                        masterkey_title: 'Master Key for TCF Canada',
                        function_description_1: '全球考點真實聽力和閱讀測試，包含測試和訓練模式',
                        function_description_2: '聽力原文和題目解析由最好的 AI 模型生成',
                        function_description_3: '學習筆記收集題目和主題以供後續複習',
                        function_description_4: 'AI 輔助工具生成您自己的寫作和口語主題',
                    },
                    profile: {
                        no_plan: "未訂閱",
                        membership_intro: "您的完整訪問權限將持續到",
                        extend_membership: "延長會員資格",
                        update_password: "更新密碼",
                        current_password: '當前密碼',
                        new_password: '新密碼',
                        confirm_new_password: '確認新密碼',
                        account: "帳戶",
                        check_subscription: "查看訂閱頁面",
                        non_membership_header: "訂閱以訪問以下資源",
                        membership_header: "您是會員，可以訪問以下資源",
                        membership_right_1: "36個真實的 TCF Canada 聽力測試 + 無限 AI 輔導",
                        membership_right_2: "37個真實的 TCF Canada 閱讀測試 + 無限 AI 輔導",
                        membership_right_3: "來自真實 TCF 考試的最新口語題 + AI 評估和輔導",
                        membership_right_4: "來自真實 TCF 考試的最新寫作話題 + AI 評估和輔導",
                        change_password_field_missing: "缺少必填字段",
                        password_not_match: "密碼不匹配",
                        change_password_failed: "密碼更改失敗",
                    },
                    comprehension: {
                        comprehension_test_structure: '客觀測試題目結構',
                        become_member_intro: "您需要成為會員才能訪問所有的試題。",
                        become_member_button: "成為會員",
                        listening_test_label_prefix: "CO - 聽力測試 ",
                        reading_test_label_prefix: "CE - 閱讀測試 ",
                        test: "測試",
                        train: "練習",
                    },
                    test_question: {
                        previous_question: "上一題",
                        next_question: "下一題",
                        transcript: "原文",
                        breakdown: "詳解",
                        show_transcript: "顯示原文",
                        show_breakdown: "顯示詳解",
                        original_text: "原文",
                        translation: "翻譯",
                        key_vocabulary_and_expressions: "關鍵詞彙和表達",
                        reference_answer: "參考答案",
                        show_analysis: "顯示分析",
                        summary: "總結",
                        translation_analysis: "翻譯和分析",
                        options: '選項',
                        explanation: '解釋',
                        conclusion: '結論',
                        finish: "完成",
                        confirm_finish_test: "確認完成測試？",
                    },
                    test_result: {
                        test_result: "測試結果",
                        correct: "正確",
                        wrong: "錯誤",
                        answered: "已回答",
                        not_answered: "未回答",
                        total_score: "總分",
                        score: "得分",
                        correct_answer: "正確答案",
                        your_answer: "您的答案",
                        under_levels: "低於 4"
                    },
                    subjective_listing: {
                        no_tags: "沒有標籤",
                        no_summary: "暫無總結",
                        failed_load_data: '載入資料失敗',
                        membership_required: '需要會員權限',
                        upgrade_membership_prompt: '請升級會員以訪問此任務。',
                        loading: '載入中...',
                        become_member_all_tasks: '您需要成為會員才能訪問所有口語題目。',
                        completed: '已完成',
                        no_content: '暫無內容',
                    },
                    subjective_detail: {
                        writing: "寫作",
                        speaking: "口語",
                        subject: "話題",
                        word_limitation: "(最少120字 / 最多150字)",
                        writing_task_1_word_limitation: "(最少60字 / 最多120字)",
                        writing_task_2_word_limitation: "(最少120字 / 最多150字)",
                        writing_task_3_word_limitation: "(最少120字 / 最多180字)",
                        speaking_task_2_word_limitation: "(3分30秒)",
                        speaking_task_3_word_limitation: "(4分30秒)",
                        AI_release_soon: "AI寫作即將推出...",
                        failed_load_question: '載入題目失敗',
                        enter_answer: '請輸入您的答案',
                        answer_submitted_success: '答案提交成功',
                        failed_submit_answer: '提交答案失敗',
                        loading_question: '正在載入題目...',
                        question_label: '題目',
                        word_limit: '字數限制：{{wordLimit}}',
                        custom_word_limitation: '(最少 {{min}} 字 / 最多 {{max}} 字)',
                        document_1: '材料 1：',
                        document_2: '材料 2：',
                        your_answer: '你的答案',
                        writing_placeholder: '請在此輸入你的寫作答案...',
                        speaking_placeholder: '請在此輸入你的口語草稿...',
                        submitting: '提交中...',
                        submit_answer: '提交答案',
                    },
                    pagination: {
                        go_to_page: "跳轉到頁面",
                    },
                    footer: {
                        platform_intro: 'TCF Canada 英語和法語語言能力測試備考平台。',
                        contact_us: '聯繫我們',
                        faq: '常見問題',
                        faq_link_1: '幫助中心',
                        faq_link_2: '支持',
                        social_media: '社交媒體',
                        follow_us: 'FOLLOW US',
                        red_note: '小紅書',
                        wechat_label: '微信',
                        all_rights_reserved: '版權所有。',
                    },
                    admin: adminTranslations['zh-CHZ'],
                }
            },
            ar: {
                translation: arTranslation,
            },
        }
    });

i18n.on('languageChanged', syncDocumentLanguage);
syncDocumentLanguage(i18n.language);

export default i18n;
